import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import FloatingMacarons from "../components/FloatingMacarons";

const FAQS = [
  {
  q: "Are you food safety certified?",
  a: "Yes! We hold a current Food Handler's Certificate and take food safety seriously.",
  },
  {
    q: "Are all your macarons gluten free?",
    a: "Yes! Every single product we make is 100% gluten free. Our kitchen is a dedicated gluten-free environment, meaning we never bring gluten-containing products into our baking space. Our macarons are made with almond flour and contain no wheat, barley, or rye.",
  },
  {
    q: "How long do macarons stay fresh?",
    a: "Macarons are best enjoyed within 3–5 days of pickup when stored in an airtight container in the refrigerator. Bring them to room temperature for 15 minutes before eating for the best flavor and texture.",
  },
  {
    q: "Can I freeze macarons?",
    a: "Absolutely! Macarons freeze beautifully. Store them in an airtight container or bag in the freezer for up to 2 months. Thaw in the refrigerator overnight or at room temperature for 30 minutes.",
  },
  {
    q: "Do I need to create an account to order?",
    a: "No account needed! We use a simple guest checkout. Just add items to your cart, enter your contact info, and pay securely.",
  },
  {
    q: "Is payment secure?",
    a: "Yes! All payments are processed securely through Stripe, a PCI-compliant payment processor trusted by millions of businesses. We never store your credit card information on our end — it goes directly through Stripe's encrypted system.",
  },
  {
    q: "How do I pick up my order?",
    a: "We are a home bakery with local pickup only at this time. Pickup details (date, time, and address) will be confirmed via email after checkout.",
  },
];

const LEGAL = [
  {
    q: "Payment Policy",
    a: "Full payment is required at the time of ordering to secure your spot. Orders are not confirmed until payment is received.",
  },
 {
    q: "Cancellation Policy",
    a: "Due to the made-to-order nature of our products, cancellations or refund requests are not accepted after an order is placed.",
  },
  {
    q: "Cottage Food Notice",
    a: "These products are made in a home kitchen and are not inspected by the Texas Department of State Health Services, as permitted under the Texas Cottage Food Law.",
  },
  {
    q: "Allergen Notice",
    a: "Our macarons are 100% gluten free and produced in a dedicated gluten-free kitchen. Products contain eggs, dairy, and tree nuts (almond flour), and may contain soy. While every care is taken to avoid cross-contamination, please order at your own discretion if you have additional allergies or sensitivities.",
  },
  {
    q: "Liability Disclaimer",
    a: "By purchasing and consuming our products, you agree that Stewart's Sweet Shells is not legally liable for any allergic reactions that may result from consumption. Customers assume full responsibility for verifying ingredients and determining whether products are safe for their dietary needs.",
  },
];

// Hero macarons — 3 per side, matches About.jsx
const HERO_MACARONS = [
  { top: "15%", left: "2%",  delay: 0.0, size: 54 },
  { top: "50%", left: "8%",  delay: 1.2, size: 46 },
  { top: "80%", left: "2%",  delay: 2.4, size: 50 },
  { top: "15%", left: "92%", delay: 0.6, size: 50 },
  { top: "50%", left: "84%", delay: 1.8, size: 44 },
  { top: "80%", left: "91%", delay: 3.0, size: 52 },
];

// Content macarons — identical to About.jsx
const CONTENT_MACARONS = [
  { top: "3%",  left: "8%",  delay: 0.2, size: 44 },
  { top: "14%", left: "2%",  delay: 1.5, size: 34 },
  { top: "25%", left: "8%",  delay: 0.8, size: 50 },
  { top: "36%", left: "2%",  delay: 2.0, size: 36 },
  { top: "47%", left: "8%",  delay: 0.4, size: 44 },
  { top: "58%", left: "2%",  delay: 1.3, size: 34 },
  { top: "69%", left: "8%",  delay: 0.9, size: 48 },
  { top: "80%", left: "2%",  delay: 1.7, size: 36 },
  { top: "91%", left: "8%",  delay: 0.5, size: 42 },
  { top: "3%",  left: "84%", delay: 0.6, size: 40 },
  { top: "14%", left: "92%", delay: 1.9, size: 50 },
  { top: "25%", left: "84%", delay: 0.1, size: 34 },
  { top: "36%", left: "92%", delay: 2.3, size: 46 },
  { top: "47%", left: "84%", delay: 0.7, size: 36 },
  { top: "58%", left: "92%", delay: 1.4, size: 52 },
  { top: "69%", left: "84%", delay: 0.3, size: 34 },
  { top: "80%", left: "92%", delay: 2.1, size: 44 },
  { top: "91%", left: "84%", delay: 1.0, size: 38 },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-cream-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center py-5 text-left gap-4 group"
      >
        <span className="font-body font-bold text-sage-800 text-sm group-hover:text-sage-600 transition-colors">
          {q}
        </span>
        <ChevronDown
          size={18}
          className={`text-sage-500 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 pb-5" : "max-h-0"}`}>
        <p className="text-gray-500 text-sm leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="pt-20">

      {/* Hero */}
      <section className="relative text-center py-16 px-4 bg-gradient-to-b from-cream-100 to-cream-50 overflow-hidden">
        <FloatingMacarons positions={HERO_MACARONS} heroOnly />
        <p className="font-handwrite text-sage-500 text-xl mb-2">Got questions?</p>
        <h1 className="font-display text-4xl text-sage-900 mb-3">FAQ</h1>
        <p className="text-gray-500 text-sm max-w-sm mx-auto">
          Answers to our most common questions.
        </p>
      </section>

      {/* FAQ + Legal — one continuous section */}
      <section className="relative bg-cream-50 pt-2 pb-16 px-4 overflow-hidden">
        <FloatingMacarons positions={CONTENT_MACARONS} />
        <div className="relative max-w-2xl mx-auto space-y-8">

          {/* General FAQs */}
          <div>
            <div className="card px-6 py-2">
              {FAQS.map((faq, i) => (
                <FAQItem key={i} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>

          {/* Soft divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-cream-200" />
            <div className="flex-1 h-px bg-cream-200" />
          </div>

          {/* Legal & Policies */}
          <div>
            <p className="font-handwrite text-sage-500 text-xl mb-1 text-center">The fine print</p>
            <h2 className="font-display text-4xl text-sage-800 text-center mb-2">Legal & Policies</h2>
            <p className="text-gray-500 text-sm max-w-sm text-center mx-auto">
              By placing an order, you agree to the following terms.
            </p>
            <br/>
            <br/>
            <div className="card px-6 py-2">
              {LEGAL.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm mb-2">Still have questions?</p>
            <a href="mailto:hello@stewartsweetshells.com" className="btn-outline inline-block">
              Email Us
            </a>
          </div>
          <div>
            <p className="text-xs text-gray-400 text-center mt-6 leading-relaxed">
              Stewart's Sweet Shells is a licensed Texas Cottage Food operation.
            </p>
            </div>
        </div>
      </section>
    </div>
  );
}
